import React, {FC} from 'react'

import {useSubscription} from '../../../api/subscriptions/hooks'
import {
  ActivityGroupTypeEnum,
  CalculatedSubscription,
  Subscription,
  SubscriptionActivity,
} from '../../../api/subscriptions/types'
import {TranslatorScope} from '../../../locale/types'
import {GradientLoader} from '../../../ui/components/gradient-loader/gradient-loader'
import {TitledSection} from '../../../ui/components/titeled-section/titled-section'
import {useLocale} from '../../../utils/i18n'
import {BenefitTitle} from './benefit'
import {PassAvailability} from './pass-availability'

type Props = {
  subscriptionId?: string
  calculatedSubscription?: CalculatedSubscription | Subscription
}

const activityGroupTypeToLocaleMap: {[key in ActivityGroupTypeEnum]: TranslatorScope} = {
  [ActivityGroupTypeEnum.CLASS]: 'classes',
  [ActivityGroupTypeEnum.SERVICE]: 'services',
  [ActivityGroupTypeEnum.TEACHER]: 'teachers',
  [ActivityGroupTypeEnum.ALL]: 'allActivities',
  [ActivityGroupTypeEnum.SALES]: 'sales',
}

export const BenefitsList: FC<Props> = ({subscriptionId, calculatedSubscription}) => {
  const {data: subscription} = useSubscription(subscriptionId)
  const subscriptionToUse = calculatedSubscription || subscription
  const {l} = useLocale()
  const activities = subscriptionToUse?.activities
  if (!activities) {
    return <GradientLoader height={'150px'} />
  }
  const groupedBenefits = activities.reduce<{[key in ActivityGroupTypeEnum]: SubscriptionActivity[]}>(
    (acc, activity) => ({
      ...acc,
      [activity.groupType]: [...acc[activity.groupType], activity],
    }),
    {
      [ActivityGroupTypeEnum.CLASS]: [],
      [ActivityGroupTypeEnum.SERVICE]: [],
      [ActivityGroupTypeEnum.TEACHER]: [],
      [ActivityGroupTypeEnum.ALL]: [],
      [ActivityGroupTypeEnum.SALES]: [],
    },
  )

  return (
    <>
      <PassAvailability
        activities={activities}
        purchaseLimit={subscriptionToUse.purchaseLimit}
      />
      {(Object.keys(groupedBenefits) as ActivityGroupTypeEnum[]).map((key, i) => {
        const groupNotEmpty = Boolean(groupedBenefits[key].length)
        if (!groupNotEmpty) {
          return null
        }
        return (
          <TitledSection
            key={`${i}_${key}`}
            title={l(activityGroupTypeToLocaleMap[key])}
          >
            {groupedBenefits[key as SubscriptionActivity['groupType']].map((activity) => (
              <BenefitTitle benefit={activity} />
            ))}
          </TitledSection>
        )
      })}
    </>
  )
}
